import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Dropdown, Form, Tab } from 'semantic-ui-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useToasts } from 'common/toast';
import { Container } from 'common/components/PageTitle';
import { RecommendationType } from '../../../../common/constants/RecommendationType';
import { templatesApi } from '../api/recommendation-templates';
import { PhaseTab } from '../PhaseTab';
import { ResourceModal } from '../ResourceModal';
import cloneDeep from 'lodash/cloneDeep';
import TeamRecContextFieldsTab from './TeamRecContextFieldsTab';
import { PILLARS } from 'common/constants/pillars';
import LoadingSpinner from 'common/components/LoadingSpinner';

const newEntityState = (pillarList: any[], orgGoals: any[]) => ({
  title: '',
  description: '',
  phases: [{ activities: [] }, { activities: [] }, { activities: [] }],
  associations: {
    factor: pillarList[0]?.id ?? null,
    organizationalGoal: { id: orgGoals[0]?.id ?? null },
  },
  context: {
    whyRecommendation: '',
    focusAreas: '',
    recommendationQuestion: '',
    organizationQuestions: [],
    effortRequires: [],
    involvedAreas: '',
    desiredOutcome: '',
    timeframe: '',
    cost: '',
    policyChange: '',
    cultureChange: '',
    dataCollection: '',
    communityConnections: '',
    primaryStakeholder: '',
  },
});

const TeamRecommendation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orgGoals = location.state;
  const { id: templateId } = useParams();
  const { addToast } = useToasts();

  const [modalOpen, setModalOpen] = useState(false);
  const [currentResources, setCurrentResources] = useState<any[]>([]);
  const [currentactivityIndex, setCurrentactivityIndex] = useState<number | null>(null);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState<number | null>(null);
  const [currentTaskIndex, setCurrentTaskIndex] = useState<number | null>(null);

  const [teamProject, setTeamSchedule] = useState<
    | {
        title: string;
        description: string;
        phases: any[];
        associations: { organizationalGoal: any; factor: any };
        context: any;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    if (!templateId && orgGoals?.length && PILLARS?.length) {
      setTeamSchedule(newEntityState(PILLARS, orgGoals));
    }
  }, [templateId, orgGoals]);

  const { data: existingProject, isLoading: projectIsLoading } =
    templatesApi.useApiAdminV1RecommendationTemplateRetrieveQuery(
      {
        id: Number(templateId),
      },
      { skip: !templateId }
    );

  const [triggerUpdate] = templatesApi.useApiAdminV1RecommendationTemplateUpdateMutation();

  const [triggerInsert] = templatesApi.useApiAdminV1RecommendationTemplateCreateMutation();

  useEffect(() => {
    if (existingProject) {
      setTeamSchedule({
        title: existingProject.name,
        ...cloneDeep(existingProject.template),
      });
    }
  }, [existingProject]);

  const saveTeamProject = () => {
    if (!teamProject) {
      return;
    }
    const updateId = templateId || null;
    const orgGoal = orgGoals.find(
      (orgGoal: any) => orgGoal.id === teamProject.associations.organizationalGoal.id
    );
    const pillar = (PILLARS as any).find(
      (pillar: any) => pillar.id === teamProject.associations.factor
    );

    const templatePayload = {
      id: updateId || null,
      name: orgGoal.name + ' - (' + pillar.name + ')', // org goal name + - (Pillar name)
      status: 9,
      template: teamProject,
      type_of: RecommendationType.TEAM_RECOMMENDATIONS,
      target: orgGoal.target,
    };

    if (updateId) {
      triggerUpdate({
        id: templatePayload.id,
        recommendationTemplate: templatePayload,
      } as any);
    } else {
      triggerInsert({
        id: templatePayload.id,
        recommendationTemplate: templatePayload,
      } as any);
    }

    addToast('Team Project Template Saved', {
      appearance: 'success',
      autoDismiss: true,
      autoDismissTimeout: 1500,
    });
    navigate('/settings/admin/recommendation-templates/team-recs');
  };

  const addActivity = (phaseIndex: number) => {
    if (!teamProject) {
      return;
    }
    let updatedPhases = [...teamProject.phases];
    updatedPhases[phaseIndex].activities.push({
      activityTitle: '',
      tasks: [],
    });
    setTeamSchedule({
      ...teamProject,
      phases: updatedPhases,
    });
  };
  const removeActivity = (phaseIndex: number, activityIndex: number) => {
    if (!teamProject) {
      return;
    }
    let updatedPhases = [...teamProject.phases];
    updatedPhases[phaseIndex].activities = teamProject.phases[phaseIndex].activities.filter(
      (_: any, i: number) => i !== activityIndex
    );
    setTeamSchedule({ ...teamProject, phases: updatedPhases });
  };

  const addTask = (phaseIndex: number, activityIndex: number) => {
    if (!teamProject) {
      return;
    }
    const updatedPhases = [...teamProject.phases];
    updatedPhases[phaseIndex].activities[activityIndex].tasks.push({
      task: '',
      metric: '',
      resources: [],
    });
    setTeamSchedule({ ...teamProject, phases: updatedPhases });
  };

  const removeTask = (phaseIndex: number, activityIndex: number, taskIndex: number) => {
    if (!teamProject) {
      return;
    }
    let updatedPhases = [...teamProject.phases];
    updatedPhases[phaseIndex].activities[activityIndex].tasks = updatedPhases[
      phaseIndex
    ].activities[activityIndex].tasks.filter((_: any, i: number) => i !== taskIndex);
    setTeamSchedule({ ...teamProject, phases: updatedPhases });
  };

  const openResourceModal = (
    phaseIndex: number,
    activityIndex: number,
    taskIndex: number,
    resources: any[]
  ) => {
    // This could be modal props
    setCurrentPhaseIndex(phaseIndex);
    setCurrentactivityIndex(activityIndex);
    setCurrentTaskIndex(taskIndex);
    setCurrentResources(resources);
    setModalOpen(true);
  };

  const addResource = (phaseIndex: number, newResource: any) => {
    if (!teamProject) {
      return;
    }
    if (currentactivityIndex === null || currentTaskIndex === null) {
      console.warn('Cannot add resource without activity and task index');
      return;
    }
    const updatedResources = [...currentResources, newResource];
    const updatedPhases = [...teamProject.phases];
    updatedPhases[phaseIndex].activities[currentactivityIndex].tasks[currentTaskIndex]['content'] =
      updatedResources;
    setTeamSchedule({ ...teamProject, phases: updatedPhases });
    setCurrentResources(updatedResources);
  };

  const removeResource = (phaseIndex: number, resourceIndex: number) => {
    if (!teamProject) {
      return;
    }
    if (currentactivityIndex === null || currentTaskIndex === null) {
      console.warn('Cannot remove resource without activity and task index');
      return;
    }
    const updatedResources = currentResources.filter((_, i) => i !== resourceIndex);
    const updatedPhases = [...teamProject.phases];
    updatedPhases[phaseIndex].activities[currentactivityIndex].tasks[currentTaskIndex]['content'] =
      updatedResources;
    setTeamSchedule({ ...teamProject, phases: updatedPhases });
    setCurrentResources(updatedResources);
  };

  const updateResourceInfo = (
    phaseIndex: number,
    resourceIndex: number,
    field: any,
    value: any
  ) => {
    if (!teamProject) {
      return;
    }
    if (currentactivityIndex === null || currentTaskIndex === null) {
      console.warn('Cannot update resource without activity and task index');
      return;
    }
    const updatedResources = [...currentResources];
    updatedResources[resourceIndex][field] = value;
    const updatedPhases = [...teamProject.phases];
    updatedPhases[phaseIndex].activities[currentactivityIndex].tasks[currentTaskIndex]['content'] =
      updatedResources;
    setTeamSchedule({ ...teamProject, phases: updatedPhases });
    setCurrentResources(updatedResources);
  };

  const updateActivityTitle = (phaseIndex: number, activityIndex: number, value: string) => {
    if (!teamProject) {
      return;
    }
    const updatedPhases = [...teamProject.phases];
    updatedPhases[phaseIndex].activities[activityIndex].activityTitle = value;
    setTeamSchedule({ ...teamProject, phases: updatedPhases });
  };

  const updateBCorpRelevance = (phaseIndex: number, activityIndex: number, value: string) => {
    if (!teamProject) {
      return;
    }
    const updatedPhases = [...teamProject.phases];
    updatedPhases[phaseIndex].activities[activityIndex].bCorpRelevance = value;
    setTeamSchedule({ ...teamProject, phases: updatedPhases });
  };

  const updateTaskInfo = (
    phaseIndex: number,
    activityIndex: number,
    taskIndex: number,
    field: string,
    value: string
  ) => {
    if (!teamProject) {
      return;
    }
    const updatedPhases = [...teamProject.phases];
    updatedPhases[phaseIndex].activities[activityIndex].tasks[taskIndex][field] = value;
    setTeamSchedule({ ...teamProject, phases: updatedPhases });
  };

  // Define the panes with individual phase tabs
  const panes = [
    // Phase 1 Tab
    {
      menuItem: 'Discover',
      render: () => (
        <Tab.Pane attached={false}>
          <PhaseTab
            phaseIndex={0}
            addActivity={addActivity}
            phaseData={teamProject!!.phases[0]}
            updateActivityTitle={updateActivityTitle}
            updateBCorpRelevance={updateBCorpRelevance}
            updateTaskInfo={updateTaskInfo}
            addTask={addTask}
            removeTask={removeTask}
            removeActivity={removeActivity}
            openResourceModal={openResourceModal}
          />
        </Tab.Pane>
      ),
    },
    // Phase 2 Tab
    {
      menuItem: 'Design',
      render: () => (
        <Tab.Pane attached={false}>
          <PhaseTab
            phaseIndex={1}
            addActivity={addActivity}
            phaseData={teamProject!!.phases[1]}
            updateActivityTitle={updateActivityTitle}
            updateBCorpRelevance={updateBCorpRelevance}
            updateTaskInfo={updateTaskInfo}
            addTask={addTask}
            removeTask={removeTask}
            removeActivity={removeActivity}
            openResourceModal={openResourceModal}
          />
        </Tab.Pane>
      ),
    },
    // Phase 3 Tab
    {
      menuItem: 'Deploy',
      render: () => (
        <Tab.Pane attached={false}>
          <PhaseTab
            phaseIndex={2}
            addActivity={addActivity}
            phaseData={teamProject!!.phases[2]}
            updateActivityTitle={updateActivityTitle}
            updateBCorpRelevance={updateBCorpRelevance}
            updateTaskInfo={updateTaskInfo}
            addTask={addTask}
            removeTask={removeTask}
            removeActivity={removeActivity}
            openResourceModal={openResourceModal}
          />
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Context Fields',
      render: () =>
        TeamRecContextFieldsTab({
          context: teamProject?.context,
          onChange: context =>
            setTeamSchedule({
              ...teamProject!!,
              context,
            }),
        })(),
    },
  ];

  function onResourceModalClosed() {
    setModalOpen(false);
  }

  if (projectIsLoading || !teamProject) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <StyledForm>
        <Form>
          <Form.Field>
            <label>Pillar</label>
            <Dropdown
              fluid
              selection
              options={PILLARS.map(pillar => ({
                key: pillar.id,
                text: pillar.name,
                value: pillar.id,
              }))}
              value={teamProject.associations.factor}
              onChange={(e, { value }) => {
                setTeamSchedule({
                  ...teamProject,
                  associations: {
                    ...teamProject.associations,
                    factor: value,
                  },
                });
              }}
            />
          </Form.Field>
          <Form.Field>
            <label>Organizational Goal</label>
            <Dropdown
              fluid
              selection
              options={orgGoals.map((goal: { id: any; name: any }) => ({
                key: goal.id,
                text: goal.name,
                value: goal.id,
              }))}
              value={teamProject.associations.organizationalGoal.id}
              onChange={(e, { value }) =>
                setTeamSchedule({
                  ...teamProject,
                  associations: {
                    ...teamProject.associations,
                    organizationalGoal: { id: value },
                  },
                })
              }
            />
          </Form.Field>
        </Form>
      </StyledForm>

      <Tab menu={{ secondary: true, pointing: true }} panes={panes} />

      <ResourceModal
        phaseIndex={currentPhaseIndex}
        open={modalOpen}
        onClose={onResourceModalClosed}
        resources={currentResources}
        addResource={addResource}
        removeResource={removeResource}
        updateResourceInfo={updateResourceInfo}
      />

      <SaveContainer>
        <Button onClick={() => navigate('/settings/admin/recommendation-templates/team-recs')}>
          Cancel
        </Button>
        <Button primary onClick={saveTeamProject}>
          Save Recommendation
        </Button>
      </SaveContainer>
    </Container>
  );
};

export default TeamRecommendation;

const StyledForm = styled(Form)`
  margin-bottom: 20px;
`;

const SaveContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;
